import { Check, Loader2, ShieldCheck } from "lucide-react";
import {
	AccordionHeader,
	AccordionItem,
	AccordionPanel,
	AccordionTrigger,
} from "@/components/Accordion";
import { Field, InfoLabel } from "@/components/catalyst/Fieldset";
import { Input, InputGroup } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import type {
	AttestationDraft,
	AttestationSection as AttestationSectionType,
} from "../types";

interface AttestationSectionProps {
	attestation: AttestationSectionType;
}

export function AttestationSection({ attestation }: AttestationSectionProps) {
	return (
		<AccordionItem value="attestation">
			<AccordionHeader>
				<AccordionTrigger>
					<div className="flex items-center gap-3">
						<div className="flex size-8 items-center justify-center rounded-lg bg-success/10 text-success">
							<ShieldCheck className="size-4" />
						</div>
						<div className="flex flex-col items-start">
							<span className="font-semibold">App Attestation</span>
							<span className="text-xs text-base-content/50 font-normal">
								Verify app authenticity to prevent fraud
							</span>
						</div>
					</div>
				</AccordionTrigger>
			</AccordionHeader>
			<AccordionPanel>
				<div className="flex flex-col gap-6 pt-2">
					<Field>
						<InfoLabel
							info={
								<div className="flex flex-col gap-2">
									<p>
										Ensures attribution requests come from your authentic app,
										not scripts or emulators.
									</p>
									<p>
										<span className="font-bold text-secondary">
											App attestation
										</span>{" "}
										helps prevent referral fraud by verifying the app binary at
										request time.
									</p>
									<p>
										Required for secure automatic attribution. Optional for
										explicit code claims.
									</p>
								</div>
							}
						>
							Provider
						</InfoLabel>

						<InputGroup>
							<Select
								showArrow={false}
								value={attestation.value.provider}
								className="appearance-none"
								onChange={(e) => {
									const provider = e.target
										.value as AttestationDraft["provider"];
									if (provider === "none") {
										attestation.update({ provider: "none" });
									} else {
										attestation.update({
											provider: "firebase",
											projectNumber: "",
										});
									}
								}}
							>
								<option value="none">None</option>
								<option value="firebase">Firebase App Check</option>
							</Select>

							{attestation.isSaving && (
								<Loader2
									className="h-4 w-4 animate-spin text-base-content/40"
									data-slot="icon"
								/>
							)}
							{attestation.isSaved && (
								<Check className="h-4 w-4" data-slot="icon" />
							)}
						</InputGroup>
					</Field>

					{attestation.value.provider === "firebase" && (
						<Field>
							<InfoLabel info="Firebase project number used for App Check verification.">
								Firebase Project Number
							</InfoLabel>

							<InputGroup>
								<Input
									value={attestation.value.projectNumber}
									onChange={(e) =>
										attestation.update({
											provider: "firebase",
											projectNumber: e.target.value,
										})
									}
									placeholder="123456789"
								/>

								{attestation.isSaving && (
									<Loader2
										className="h-4 w-4 animate-spin text-base-content/40"
										data-slot="icon"
									/>
								)}
								{attestation.isSaved && (
									<Check className="h-4 w-4" data-slot="icon" />
								)}
							</InputGroup>
						</Field>
					)}
				</div>
			</AccordionPanel>
		</AccordionItem>
	);
}
